"use client";

import { useEffect, useRef } from "react";
import * as THREE from "three";

/**
 * Mixed wireframe shapes (cubes, spheres, rings) slowly rotating and
 * drifting. Pure ambient background — pauses off-screen, respects
 * prefers-reduced-motion.
 */
export function MultiShape3D({
  accent = "#bff745",
  opacity = 0.2,
  shapeCount = 10,
}: {
  accent?: string;
  opacity?: number;
  shapeCount?: number;
}) {
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const reducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    const w = container.clientWidth;
    const h = container.clientHeight;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(45, w / h, 0.1, 100);
    camera.position.set(0, 0, 16);

    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: true,
      powerPreference: "low-power",
    });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(w, h);
    renderer.setClearColor(0x000000, 0);
    container.appendChild(renderer.domElement);

    const accentColor = new THREE.Color(accent);

    type Shape = {
      mesh: THREE.LineSegments;
      basePos: THREE.Vector3;
      rotSpeed: THREE.Vector3;
      driftPhase: THREE.Vector3;
      driftAmp: number;
      driftSpeed: number;
    };

    const shapes: Shape[] = [];

    const geometryBuilders: Array<() => THREE.BufferGeometry> = [
      () => new THREE.BoxGeometry(1.8, 1.8, 1.8),
      () => new THREE.IcosahedronGeometry(1.2, 0),
      () => new THREE.TorusGeometry(1.1, 0.32, 8, 24),
      () => new THREE.OctahedronGeometry(1.3, 0),
      () => new THREE.TorusKnotGeometry(0.8, 0.22, 64, 8, 2, 3),
      () => new THREE.SphereGeometry(1.1, 12, 8),
    ];

    for (let i = 0; i < shapeCount; i++) {
      const sourceGeom = geometryBuilders[i % geometryBuilders.length]();
      const edges = new THREE.EdgesGeometry(sourceGeom, 12);
      const material = new THREE.LineBasicMaterial({
        color: accentColor,
        transparent: true,
        opacity: opacity * (0.8 + (i % 3) * 0.1),
      });
      const mesh = new THREE.LineSegments(edges, material);

      // Spread shapes across the scene
      const angle = (i / shapeCount) * Math.PI * 2;
      const radius = 5.5 + (i % 4) * 1.4;
      mesh.position.set(
        Math.cos(angle) * radius,
        Math.sin(angle) * (radius * 0.5) + (i % 2 === 0 ? 0.5 : -0.5),
        -3 - (i % 5) * 0.7
      );

      const scale = 0.7 + ((i * 0.17) % 0.7);
      mesh.scale.setScalar(scale);

      scene.add(mesh);

      shapes.push({
        mesh,
        basePos: mesh.position.clone(),
        rotSpeed: new THREE.Vector3(
          0.06 + ((i * 0.03) % 0.15),
          0.05 + ((i * 0.05) % 0.18),
          0.03 + ((i * 0.02) % 0.12)
        ),
        driftPhase: new THREE.Vector3(i * 0.6, i * 1.1, i * 0.3),
        driftAmp: 0.35 + (i % 3) * 0.15,
        driftSpeed: 0.18 + (i % 4) * 0.05,
      });

      sourceGeom.dispose();
    }

    const onResize = () => {
      const nw = container.clientWidth;
      const nh = container.clientHeight;
      camera.aspect = nw / nh;
      camera.updateProjectionMatrix();
      renderer.setSize(nw, nh);
    };
    window.addEventListener("resize", onResize);

    let isVisible = true;
    const io = new IntersectionObserver(
      ([entry]) => {
        isVisible = entry.isIntersecting;
      },
      { threshold: 0 }
    );
    io.observe(container);

    let rafId = 0;
    const clock = new THREE.Clock();

    const tick = () => {
      const dt = clock.getDelta();
      const t = clock.elapsedTime;

      if (isVisible && !reducedMotion) {
        for (const s of shapes) {
          s.mesh.rotation.x += s.rotSpeed.x * dt;
          s.mesh.rotation.y += s.rotSpeed.y * dt;
          s.mesh.rotation.z += s.rotSpeed.z * dt;

          s.mesh.position.x =
            s.basePos.x +
            Math.sin(t * s.driftSpeed + s.driftPhase.x) * s.driftAmp;
          s.mesh.position.y =
            s.basePos.y +
            Math.cos(t * s.driftSpeed + s.driftPhase.y) * s.driftAmp * 0.7;
          s.mesh.position.z =
            s.basePos.z +
            Math.sin(t * s.driftSpeed * 0.5 + s.driftPhase.z) *
              (s.driftAmp * 0.4);
        }
        renderer.render(scene, camera);
      }

      rafId = requestAnimationFrame(tick);
    };
    tick();

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener("resize", onResize);
      io.disconnect();
      shapes.forEach((s) => {
        scene.remove(s.mesh);
        s.mesh.geometry.dispose();
        (s.mesh.material as THREE.LineBasicMaterial).dispose();
      });
      renderer.dispose();
      if (renderer.domElement.parentNode === container) {
        container.removeChild(renderer.domElement);
      }
    };
  }, [accent, opacity, shapeCount]);

  return (
    <div
      ref={containerRef}
      aria-hidden="true"
      className="pointer-events-none absolute inset-0"
    />
  );
}
